import arrayLength from "@-/com.npmjs.extra-array__length";

import { isProducedTokenMatch } from "#is-produced-token-match";
import {
  isScorableToken,
  type ProducedToken,
  type SentenceDrillToken,
} from "#sentence-drill-token";

export type UtteranceAlignment = {
  /**
   * Whether the run ended because the user got something WRONG, as opposed to simply stopping.
   *
   * The difference decides whether a hint is owed. A user who trails off mid-sentence has not
   * failed at anything — escalating there would burn a hint on a pause. A user who kept going and
   * said the wrong word HAS hit the thing they don't know. Distinguishable because the run's length
   * is compared against how much they actually produced.
   */
  endedOnMismatch: boolean;
  /** Indices of the sentence tokens this utterance satisfied, ascending. */
  matchedIndexList: readonly number[];
};

/**
 * Align what the user produced against the sentence, anywhere in it.
 *
 * The user may restart the whole sentence, resume from the blank they stopped at, or pick up from
 * some arbitrary point in between — all three are the same operation: find where this utterance
 * begins in the sentence, then consume forward for as long as it keeps matching.
 *
 * The "longest correct prefix" the drill is specified in terms of is just the special case where
 * the alignment happens to start at index 0; it needs no separate code path.
 *
 * Ties are broken toward the candidate nearest `cursorIndex` — the blank the user was actually
 * asked for — so a word that occurs twice in a sentence resolves to the occurrence they are
 * plausibly working on rather than the first one textually. A second tie-break on the lower index
 * keeps the result deterministic.
 *
 * Returns `null` when the utterance's first token matches nowhere, i.e. the very first thing they
 * said was wrong. Callers treat that as "no progress", NOT as a failed attempt at the cursor —
 * garbage from the recognizer must not cost the user a hint.
 */
export function alignUtterance({
  cursorIndex,
  produced,
  tokens,
}: {
  cursorIndex: number;
  produced: readonly ProducedToken[];
  tokens: readonly SentenceDrillToken[];
}): UtteranceAlignment | null {
  const firstProduced = produced[0];
  if (firstProduced === undefined) {
    return null;
  }

  let best: { distance: number; matchedIndexList: number[]; startIndex: number } | null = null;

  for (let startIndex = 0; startIndex < arrayLength(tokens); startIndex++) {
    const startToken = tokens[startIndex];
    if (startToken === undefined || !isScorableToken(startToken)) {
      continue;
    }
    if (!isProducedTokenMatch(startToken, firstProduced)) {
      continue;
    }

    // Extend greedily from this anchor, stepping over non-scorable tokens (punctuation) so a
    // comma mid-sentence never breaks a run the user spoke straight through.
    const matchedIndexList: number[] = [];
    let producedIndex = 0;
    for (
      let tokenIndex = startIndex;
      tokenIndex < arrayLength(tokens) && producedIndex < arrayLength(produced);
      tokenIndex++
    ) {
      const token = tokens[tokenIndex];
      if (token === undefined) {
        break;
      }
      if (!isScorableToken(token)) {
        matchedIndexList.push(tokenIndex);
        continue;
      }
      const producedToken = produced[producedIndex];
      if (producedToken === undefined || !isProducedTokenMatch(token, producedToken)) {
        break;
      }
      matchedIndexList.push(tokenIndex);
      producedIndex++;
    }

    const distance = Math.abs(startIndex - cursorIndex);
    const isBetter =
      best === null ||
      producedIndex > countScorable(best.matchedIndexList, tokens) ||
      (producedIndex === countScorable(best.matchedIndexList, tokens) &&
        (distance < best.distance || (distance === best.distance && startIndex < best.startIndex)));
    if (isBetter) {
      best = { distance, matchedIndexList, startIndex };
    }
  }

  if (best === null) {
    return null;
  }

  const consumed = countScorable(best.matchedIndexList, tokens);
  return {
    // The user produced MORE than the run consumed, so the token after the run is one they got
    // wrong — they kept talking, and what came next did not fit. If the run consumed everything
    // they said, they simply stopped.
    endedOnMismatch: consumed < arrayLength(produced),
    matchedIndexList: best.matchedIndexList,
  };
}

function countScorable(
  matchedIndexList: readonly number[],
  tokens: readonly SentenceDrillToken[],
): number {
  let count = 0;
  for (const index of matchedIndexList) {
    const token = tokens[index];
    if (token !== undefined && isScorableToken(token)) {
      count++;
    }
  }
  return count;
}
